// Print helpers for Workmen: WORKER'S SCREENING FORM (PDF format)
// + IDENTITY CARD (Front & Back), per NCC Limited standard.
//
// Both functions open a fresh print-ready HTML window and trigger window.print().
import QRCode from "qrcode";

// Branded screening-form header (cropped from official NCCL/HSE/CORP/MNL/V3/FMT67 PDF).
// Served from frontend/public so a fresh print window can load it.
const SCREENING_HEADER_PATH = "/screening-header.png";
// Official NCC Limited corporate wordmark (multi-colour wheel inside middle C).
const NCC_LOGO_PATH = "/ncc-limited-logo.png";

async function _qrDataUrl(url) {
  try { return await QRCode.toDataURL(url, { margin: 0, width: 140 }); } catch { return ""; }
}

function esc(s) {
  if (s == null) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function maskedAadhaar(num) {
  const d = String(num || "").replace(/\D/g, "");
  if (d.length !== 12) return num || "";
  return `XXXX XXXX ${d.slice(-4)}`;
}

function openPrintWindow(html, title = "Print") {
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) {
    alert("Pop-up blocked. Please allow pop-ups for this site to print.");
    return;
  }
  w.document.open();
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>${html}</head></html>`);
  w.document.close();
  // Wait for all images (header + photo + client logo) to load before printing,
  // otherwise the browser prints a blank header.
  const triggerPrint = () => {
    try { w.focus(); w.print(); } catch (e) { /* user closed */ }
  };
  w.addEventListener("load", () => {
    const imgs = Array.from(w.document.images || []);
    if (imgs.length === 0) return triggerPrint();
    let pending = imgs.length;
    const done = () => { if (--pending <= 0) setTimeout(triggerPrint, 120); };
    imgs.forEach((im) => {
      if (im.complete && im.naturalWidth > 0) done();
      else { im.addEventListener("load", done); im.addEventListener("error", done); }
    });
    // Safety net: print after 2.5s even if some image stalls
    setTimeout(triggerPrint, 2500);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. WORKER'S SCREENING FORM — NCCL/HSE/CORP/MNL/V3/FMT67
// ─────────────────────────────────────────────────────────────────────────────
export function printScreeningForm(w, contractor) {
  const idLabel = w.id_proof_type
    ? `${w.id_proof_type}: ${w.id_proof_type === "Aadhaar" ? maskedAadhaar(w.id_proof_number || w.aadhaar) : (w.id_proof_number || "")}`
    : (w.aadhaar ? `Aadhaar: ${maskedAadhaar(w.aadhaar)}` : "");

  const rows = [
    ["__SECTION__", "Personal and other Details"],
    ["1", "Name of the Person", esc(w.name)],
    ["2", "Date Of Birth", esc(w.date_of_birth)],
    ["3", "Age", w.age_years != null ? `${w.age_years} yrs` : ""],
    ["4", "Gender", esc(w.gender)],
    ["5", "Blood Group", esc(w.blood_group)],
    ["6", "ID Proof (Aadhar/Pan Etc.,)", esc(idLabel)],
    ["7", "Trade", esc(w.trade)],
    ["8", "Experience", w.experience_years != null ? `${w.experience_years} yrs` : ""],
    ["9", "Address", esc([w.address, w.village, w.city, w.state, w.pincode].filter(Boolean).join(", "))],
    ["10", "Married/Single/Widow/ Widower", esc(w.marital_status)],
    ["11", "Number of Children", w.children_count != null ? String(w.children_count) : ""],
    ["12", "Mother Tongue", esc(w.mother_tongue)],
    ["13", "Other Languages Known", esc(w.other_languages)],
    ["14", "In case of emergency, person to be contacted and his/her contact number",
      esc([w.emergency_contact_name, w.emergency_contact_number].filter(Boolean).join(" · "))],
    ["__SECTION__", "Physical Condition"],
    ["15", "Vision", esc(w.vision)],
    ["16", "Height", esc(w.height)],
    ["17", "Weight", esc(w.weight)],
    ["18", "Hearing", esc(w.hearing)],
    ["19", "Any physical deformities", esc(w.physical_deformities)],
    ["20", "Any medical history", esc(w.medical_history)],
  ];

  const html = `
<style>
  @page { size: A4; margin: 8mm 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  html, body { margin: 0; padding: 0; font-family: "Times New Roman", Times, serif; color: #000; font-size: 10.5pt; }
  .sheet { width: 100%; max-width: 190mm; margin: 0 auto; }
  .branded-hdr { width: 100%; max-height: 30mm; overflow: hidden; }
  .branded-hdr img { display: block; width: 100%; max-height: 30mm; height: auto; object-fit: contain; }
  .sub-row { display: flex; justify-content: flex-end; font-size: 9.5pt; margin: 1.5mm 0 2mm; color: #1e3a8a; }
  .sub-row b { color: #000; margin-left: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 10pt; table-layout: fixed; }
  th, td { border: 0.4mm solid #000; padding: 1.2mm 2mm; vertical-align: top; word-wrap: break-word; line-height: 1.25; }
  th { background: #e8e8e8; text-align: left; font-weight: 700; padding: 1.2mm 2mm; }
  col.c-sno { width: 13mm; }
  col.c-part { width: 74mm; }
  col.c-val  { width: auto; }
  .sno { text-align: center; font-weight: 600; }
  .section-row td {
    background: #1e3a8a !important;
    color: #fff !important;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 10pt;
    padding: 1mm 2mm;
  }
  .note { margin-top: 2.5mm; font-size: 9.5pt; line-height: 1.3; border: 0.3mm dashed #888; padding: 1.8mm 2.5mm; background: #fafafa; }
  .sig { margin-top: 3mm; font-size: 10pt; }
  .sig .line { display: inline-block; min-width: 90mm; border-bottom: 0.3mm solid #000; margin-left: 3mm; }
  .approval { margin-top: 2.5mm; font-size: 10pt; }
  .approval .row { margin: 2.8mm 0; }
  .approval .line { display: inline-block; min-width: 95mm; border-bottom: 0.3mm solid #000; margin-left: 3mm; }
  .ftr { margin-top: 3mm; display: flex; justify-content: space-between; font-size: 8.5pt; color: #666; }
  .val { min-height: 4mm; }
  @media print {
    .sheet { max-width: none; }
  }
</style>
<body>
  <div class="sheet">
    <div class="branded-hdr">
      <img src="${SCREENING_HEADER_PATH}" alt="NCC HSE Management System Manual — Worker's Screening Form" />
    </div>
    <div class="sub-row">Sub-Contractor: <b>${esc(contractor?.name || "")}</b></div>
    <table>
      <colgroup>
        <col class="c-sno" />
        <col class="c-part" />
        <col class="c-val" />
      </colgroup>
      <thead>
        <tr><th class="sno">S. No.</th><th>Particulars</th><th>Details</th></tr>
      </thead>
      <tbody>
        ${rows.map((r) => {
          if (r[0] === "__SECTION__") {
            return `<tr class="section-row"><td colspan="3">${esc(r[1])}</td></tr>`;
          }
          return `<tr>
            <td class="sno">${esc(r[0])}</td>
            <td>${esc(r[1])}</td>
            <td class="val">${r[2] || "&nbsp;"}</td>
          </tr>`;
        }).join("")}
      </tbody>
    </table>
    <div class="note">
      <b>Note:</b> After successfully completing the initial screening, workers will proceed to medical physical fitness evaluation as per the BOCW Act (Form-XI), followed by HSE induction, ID card issuance, PPE distribution, and deployment at the site. If a screening form that includes these details is already available with the site admin, it can be used instead of this one.
    </div>
    <div class="sig">Signature or Thumb impression of the worker:<span class="line"></span></div>
    <div class="approval">
      <div class="row">Assessed by: Name and Signature of Admin Personnel <span class="line"></span></div>
      <div class="row">Reviewed by: Name and Signature of HSE Personnel: <span class="line"></span></div>
      <div class="row">Approved By: Name and Signature of Project In-charge/Manager: <span class="line"></span></div>
    </div>
    <div class="ftr">
      <div>Sensitivity: NCC Limited internal use</div>
      <div>Page 1 of 1</div>
    </div>
  </div>
</body>`;
  openPrintWindow(html, `Screening Form - ${w.name || ""}`);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. IDENTITY CARD (Front + Back)
// Standard ID card: 95 × 60 mm. 2 cards (front + back of same worker) per A4 page.
// For multi-worker bulk printing, each worker gets their own A4 page with their
// front + back stacked.
// ─────────────────────────────────────────────────────────────────────────────

const TRAINING_TOPICS_LEFT = [
  "Safety Induction", "Permit to Work", "Working at Height", "CSE",
  "Excavation Safety", "Job Safety Analysis", "Flagman/Spotter", "Fire Watch", "Trained By:",
];
const TRAINING_TOPICS_RIGHT = [
  "Heavy Equipment", "Road Traffic Safety", "First Aid", "PPE",
  "Rigging/Lifting", "Heat Stress", "Hot Work/Welding", "Standby Man", "Power & Hand Tools",
];

function _validityRange(issuedAtIso) {
  // 6-month validity from the issue date (or today if none).
  const start = issuedAtIso ? new Date(issuedAtIso) : new Date();
  const end = new Date(start);
  end.setMonth(end.getMonth() + 6);
  const fmt = (d) => d.toLocaleDateString("en-GB"); // DD/MM/YYYY
  return { from: fmt(start), to: fmt(end) };
}

function _buildIdCardPair(w, contractor, siteMaster, qrDataUrl = "") {
  // Per spec: Site STRICTLY from the workman's ncc_site_name (Name of Project in Workmen Roster)
  const projectName = w.ncc_site_name || "";
  const clientName = siteMaster?.client_name || "";
  const clientLogo = siteMaster?.client_logo || "";
  const idCardNo = w.id_card_no || "";
  const { from: validFrom, to: validTo } = _validityRange(w.id_card_issued_at);

  return `
    <div class="card front">
      <div class="top">
        <div class="left-col">
          <div class="ncc-logo"><img src="${NCC_LOGO_PATH}" alt="NCC" /></div>
          ${projectName ? `<div class="site-line"><span class="label">Site :</span> ${esc(projectName)}</div>` : ""}
        </div>
        <div class="heading">IDENTITY CARD</div>
        <div class="client-wrap">
          ${clientLogo
            ? `<img src="${esc(clientLogo)}" class="client" alt="${esc(clientName || "Client")}" />`
            : `<div class="client-text">${esc(clientName)}</div>`}
          ${idCardNo ? `<div class="id-no" data-testid="idcard-no">ID No.: ${esc(idCardNo)}</div>` : ""}
        </div>
      </div>
      <div class="body">
        <div class="fields">
          <div class="row"><span class="label">Name :</span> <span class="val">${esc(w.name || "")}</span></div>
          <div class="row"><span class="label">Designation :</span> <span class="val">${esc(w.trade || "")}</span></div>
          ${((contractor?.is_payroll || w.is_payroll) ? (() => {
            const src = (w.source || contractor?.source || "").toUpperCase();
            const agency = w.source_agency_name || contractor?.source_agency_name || "";
            const display = src === "AGENCY"
              ? `AGENCY${agency ? ` · ${esc(agency)}` : ""}`
              : (src || "NCCL");
            return `<div class="row"><span class="label">Source :</span> <span class="val">${display}</span></div>`;
          })() : `<div class="row"><span class="label">Name of Sub-Contractor :</span> <span class="val">${esc(contractor?.name || "")}</span></div>`)}
          <div class="row"><span class="label">Blood Group :</span> <span class="val">${esc(w.blood_group || "")}</span></div>
          <div class="row"><span class="label">Valid :</span> <span class="val">From ${esc(validFrom)} To ${esc(validTo)}</span></div>
          ${qrDataUrl ? `
          <div class="qr-row">
            <img class="qr" src="${qrDataUrl}" alt="QR" />
            <div class="qr-txt">SCAN TO VERIFY<br/>GATE PASS</div>
          </div>` : ""}
        </div>
        <div class="photo-wrap">
          <div class="photo">${w.photo_base64 ? `<img src="${esc(w.photo_base64)}" alt="" />` : `<span style="color:#888;font-size:8pt">PHOTO</span>`}</div>
          <div class="auth">Authorised Signatory</div>
        </div>
      </div>
    </div>

    <div class="card back">
      <div class="title">TRAINING &amp; AUTHORIZATION CARD</div>
      <table>
        <tbody>
          ${TRAINING_TOPICS_LEFT.map((t, i) => `
            <tr>
              <td class="tick"></td>
              <td class="topic">${esc(t)}</td>
              <td class="tick"></td>
              <td class="topic">${esc(TRAINING_TOPICS_RIGHT[i] || "")}</td>
            </tr>
          `).join("")}
        </tbody>
      </table>
    </div>`;
}

const _ID_CARD_CSS = `
<style>
  @page { size: A4; margin: 10mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; color-adjust: exact; }
  body { font-family: Arial, Helvetica, sans-serif; color: #000; margin: 0; }
  .page { display: flex; flex-direction: column; gap: 6mm; align-items: center; page-break-after: always; }
  .page:last-of-type { page-break-after: auto; }
  .card { width: 95mm; height: 60mm; border: 1.5px solid #000; padding: 3mm 4mm; background: #fff; }
  .front .top { display: grid; grid-template-columns: 30mm 1fr 26mm; align-items: start; column-gap: 2mm; margin-bottom: 1.5mm; }
  .front .left-col { display: flex; flex-direction: column; align-items: flex-start; gap: 1mm; }
  .front .ncc-logo { height: 11mm; display: flex; align-items: center; }
  .front .ncc-logo img { height: 11mm; width: auto; max-width: 30mm; display: block; object-fit: contain; }
  .front .site-line { font-size: 9pt; color: #0a2756; font-weight: 800; line-height: 1.15; word-wrap: break-word; max-width: 30mm; }
  .front .site-line .label { font-weight: 900; }
  .front .heading { text-align: center; color: #0a2756; font-weight: 900; font-size: 14pt; line-height: 1.1; letter-spacing: 0.8px; padding-top: 2mm; }
  .front .client-wrap { display: flex; flex-direction: column; justify-content: flex-start; align-items: flex-end; gap: 1mm; min-height: 14mm; }
  .front .client { max-height: 12mm; max-width: 26mm; object-fit: contain; }
  .front .client-text { text-align: right; font-size: 8pt; font-weight: 700; color: #0a2756; line-height: 1.1; }
  .front .id-no { font-size: 7.5pt; font-weight: 800; color: #0a2756; text-align: right; line-height: 1.15; word-break: break-all; max-width: 30mm; }
  .front .body { display: grid; grid-template-columns: 1fr 25mm; gap: 3mm; align-items: start; margin-top: 1mm; }
  .front .fields { font-size: 8.5pt; line-height: 1.55; }
  .front .fields .row { display: flex; align-items: baseline; margin-bottom: 0.6mm; }
  .front .fields .label { font-weight: 700; white-space: nowrap; margin-right: 1.5mm; }
  .front .fields .val { flex: 1; border-bottom: 0.3mm solid #000; min-height: 4mm; padding: 0 1mm; word-break: break-word; }
  .front .qr-row { display: flex; align-items: center; gap: 2mm; margin-top: 0.6mm; }
  .front .qr-row .qr { width: 8mm; height: 8mm; display: block; }
  .front .qr-row .qr-txt { font-size: 6pt; font-weight: 800; color: #0a2756; line-height: 1.2; letter-spacing: 0.3px; }
  .front .photo-wrap { width: 25mm; display: flex; flex-direction: column; align-items: center; }
  .front .photo { width: 25mm; height: 30mm; border: 0.5mm solid #000; background: #fafafa; display: grid; place-items: center; overflow: hidden; }
  .front .photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .front .auth { text-align: center; font-size: 7.5pt; margin-top: 1mm; font-weight: 600; }
  .back .title { text-align: center; color: #0a2756; font-weight: 800; font-size: 10pt; letter-spacing: 0.5px; border: 0.4mm solid #0a2756; padding: 1mm; margin-bottom: 1.5mm; }
  .back table { width: 100%; border-collapse: collapse; font-size: 7.5pt; }
  .back td { border: 0.4mm solid #000; padding: 0.8mm 1.5mm; }
  .back td.tick { width: 6mm; height: 4.5mm; text-align: center; }
  .back td.topic { font-weight: 700; color: #0a2756; }
  @media print { .no-print { display: none; } body { margin: 0; } }
  .no-print { text-align: center; margin: 8mm 0; }
  .no-print button { padding: 6px 16px; font-size: 11pt; cursor: pointer; }
</style>`;

export async function printIdentityCard(w, contractor, siteMaster) {
  // Single-worker mode: front + back stacked on one page
  const qr = await _qrDataUrl(`${window.location.origin}/workman-verify/${w.id}`);
  const html = `${_ID_CARD_CSS}
<body>
  <div class="no-print"><button onclick="window.print()">Print</button></div>
  <div class="page">
    ${_buildIdCardPair(w, contractor, siteMaster, qr)}
  </div>
</body>`;
  openPrintWindow(html, `ID Card - ${w.name || ""}`);
}

/**
 * Print multiple workmen ID cards in bulk.
 * Each A4 page contains 2 cards (front + back) for ONE worker.
 * Multiple workers print on consecutive pages (page-break-after each .page).
 *
 * @param workmen      array of workman objects
 * @param contractor   shared contractor object
 * @param siteMasters  array of NCC-Site masters used to look up client logo per workman
 */
export async function printIdentityCardsBulk(workmen, contractor, siteMasters = []) {
  if (!workmen || workmen.length === 0) {
    alert("No workmen selected for printing.");
    return;
  }
  const siteFor = (w) => {
    const name = w.ncc_site_name || contractor?.ncc_site_name;
    return (siteMasters || []).find((s) => s.name === name) || null;
  };
  const cards = await Promise.all(workmen.map(async (w) => {
    const qr = await _qrDataUrl(`${window.location.origin}/workman-verify/${w.id}`);
    return `
    <div class="page">${_buildIdCardPair(w, contractor, siteFor(w), qr)}</div>
  `;
  }));
  const pages = cards.join("");
  const html = `${_ID_CARD_CSS}
<body>
  <div class="no-print"><button onclick="window.print()">Print ${workmen.length} ID Card${workmen.length > 1 ? "s" : ""}</button></div>
  ${pages}
</body>`;
  openPrintWindow(html, `ID Cards × ${workmen.length}`);
}

// ============================================================
//  FORM-XI · CERTIFICATE OF MEDICAL EXAMINATION (BOCW Rule 223(c))
//  Generates a one-page statutory medical-examination certificate
//  populated from the saved workman record (Physical Examination
//  + Enquiry of Previous History sub-fields).
// ============================================================
const _FORM_XI_CSS = `
<style>
  /* A4 portrait — equal 10mm margins on all sides so content centres correctly */
  @page { size: A4 portrait; margin: 10mm 10mm; }
  html, body { margin: 0 !important; padding: 0 !important; }
  body { font-family: 'Times New Roman', serif; color: #000; font-size: 11px; }
  /* Wrapper sits flush with the @page margin box — no extra max-width / auto-margins (those can
     compound and look "off-centre" when printed). The page itself is the centring container. */
  .fxi-wrap { width: 100%; box-sizing: border-box; padding: 0; margin: 0; min-height: calc(297mm - 22mm); display: flex; flex-direction: column; }
  .fxi-top { display: flex; align-items: center; gap: 10px; border-bottom: 2px solid #000; padding-bottom: 6px; }
  .fxi-top img { height: 38px; }
  .fxi-top .org { font-weight: 700; font-size: 13px; line-height: 1.1; }
  .fxi-top .sub { font-size: 10px; color: #333; }
  .fxi-head { text-align: center; margin: 8px 0 4px; }
  .fxi-head .t1 { font-weight: 700; font-size: 14px; letter-spacing: 1px; }
  .fxi-head .t2 { font-size: 10px; font-style: italic; }
  .fxi-head .t3 { font-weight: 700; font-size: 13px; text-decoration: underline; margin-top: 2px; }
  .fxi-row { display: flex; gap: 12px; margin-top: 6px; }
  .fxi-row .cell { flex: 1; font-size: 11px; }
  .fxi-row .cell .lbl { font-weight: 600; }
  .fxi-row .cell .val { border-bottom: 1px dotted #000; min-height: 14px; padding: 0 2px; }
  .fxi-line { margin-top: 4px; font-size: 11px; }
  .fxi-line .lbl { font-weight: 600; display: inline-block; min-width: 200px; }
  .fxi-line .val { border-bottom: 1px dotted #000; display: inline-block; min-width: 220px; padding: 0 2px; }
  .fxi-line.full .val { min-width: 380px; }
  .fxi-fit { margin-top: 6px; font-size: 11px; line-height: 1.4; text-align: justify; padding: 4px 6px; border: 1px solid #000; background: #fafafa; }
  .fxi-fit .ph { font-style: italic; color: #444; }
  table.fxi-tbl { width: 100%; border-collapse: collapse; margin-top: 8px; table-layout: fixed; }
  table.fxi-tbl th, table.fxi-tbl td { border: 1px solid #000; padding: 3px 5px; vertical-align: top; word-wrap: break-word; }
  table.fxi-tbl th { background: #e8eef5; font-size: 11px; text-align: center; }
  table.fxi-tbl td.k { width: 26%; font-weight: 600; background: #f6f6f6; }
  table.fxi-tbl td.v { width: 24%; }
  .fxi-reason { margin-top: 8px; }
  .fxi-sigs { margin-top: auto; padding-top: 24px; display: flex; gap: 24px; }
  .fxi-sigs .box { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 10px; text-align: center; }
  .fxi-foot { margin-top: 8px; font-size: 9px; color: #333; border-top: 1px dashed #777; padding-top: 4px; }
  .fxi-foot ol { margin: 2px 0 0 14px; padding: 0; }
  .no-print { padding: 8px; background: #f3f4f6; text-align: center; }
  .no-print button { padding: 6px 14px; background: #0b1f3a; color: #fff; border: 0; border-radius: 4px; cursor: pointer; font-family: inherit; font-size: 12px; letter-spacing: .5px; }
  @media print {
    .no-print { display: none !important; }
    .fxi-wrap { page-break-inside: avoid; }
  }
</style>
`;

function _formXiSerial(w) {
  // Stable, short serial derived from workman id + ID-card no (if issued)
  const base = (w.id_card_no || w.id || "").toString().replace(/[^A-Z0-9]/gi, "").toUpperCase();
  return base ? base.slice(-10) : "—";
}

function _buildFormXI(w, contractor) {
  const today = new Date().toLocaleDateString("en-GB"); // dd/mm/yyyy
  const project = w.ncc_site_name || contractor?.ncc_site_name || "—";
  const serial = _formXiSerial(w);
  const ageStr = w.age_years != null && w.age_years !== "" ? `${w.age_years}` : "—";
  const dob = w.date_of_birth ? new Date(w.date_of_birth).toLocaleDateString("en-GB") : "—";
  const addr = [w.address, w.village, w.city, w.state, w.pincode].filter(Boolean).join(", ") || "—";
  const trade = w.trade || "—";
  const father = w.father_husband_name || "—";
  const sex = w.gender || "—";
  const name = w.name || "—";
  const idMark1 = w.phys_exam_disability && w.phys_exam_disability !== "None" ? w.phys_exam_disability : "Nil";
  const idMark2 = w.physical_deformities ? "(see Physical Examination)" : "Nil";

  const phyExam = [
    ["a) Height", w.height],
    ["b) Weight", w.weight],
    ["c) Blood Pressure", w.phys_exam_blood_pressure],
    ["d) Pulse", w.phys_exam_pulse],
    ["e) Hearing", w.hearing],
    ["f) Refractive Error", w.phys_exam_refractive_error],
    ["g) Colour Vision", w.phys_exam_colour_vision],
    ["h) Any Disability", w.phys_exam_disability],
    ["i) Arm Function & Grip", w.phys_exam_arm_function],
    ["j) Leg & Foot Function", w.phys_exam_leg_function],
  ];
  const history = [
    ["a) Varicose", w.hist_varicose],
    ["b) Seizure", w.hist_seizure],
    ["c) Vertigo", w.hist_vertigo],
    ["d) Acrophobia", w.hist_acrophobia],
    ["e) Diabetes", w.hist_diabetes],
    ["f) Stroke", w.hist_stroke],
    ["g) Heart Diseases", w.hist_heart_diseases],
    ["h) Major Illness or Surgery", w.hist_major_illness_surgery],
    ["i) Symptoms Visible", w.hist_symptoms_visible],
    ["j) Others, if any", w.hist_others],
  ];

  const rows = phyExam.map((p, i) => {
    const h = history[i] || ["", ""];
    return `<tr>
      <td class="k">${esc(p[0])}</td>
      <td class="v">${esc(p[1] || "—")}</td>
      <td class="k">${esc(h[0])}</td>
      <td class="v">${esc(h[1] || "—")}</td>
    </tr>`;
  }).join("");

  return `
<div class="fxi-wrap">
  <div class="fxi-top">
    <img src="${NCC_LOGO_PATH}" alt="NCC" onerror="this.style.display='none'"/>
    <div>
      <div class="org">NCC LIMITED</div>
      <div class="sub">Labour Contractor Databank · Statutory Medical Examination</div>
    </div>
    <div style="margin-left:auto;text-align:right;font-size:10px;">
      <div><b>Project:</b> ${esc(project)}</div>
      <div><b>Issued:</b> ${esc(today)}</div>
    </div>
  </div>

  <div class="fxi-head">
    <div class="t1">FORM - XI</div>
    <div class="t2">[ See rule 223(c) ]</div>
    <div class="t3">CERTIFICATE OF MEDICAL EXAMINATION</div>
  </div>

  <div class="fxi-row">
    <div class="cell"><div class="lbl">Name of the Project</div><div class="val">${esc(project)}</div></div>
    <div class="cell"><div class="lbl">Certificate Serial No.</div><div class="val">${esc(serial)}</div></div>
    <div class="cell"><div class="lbl">Date</div><div class="val">${esc(today)}</div></div>
  </div>

  <div class="fxi-line"><span class="lbl">1. Name of the workman</span> : <span class="val">${esc(name)}</span></div>
  <div class="fxi-line"><span class="lbl">2. Trade of the workman</span> : <span class="val">${esc(trade)}</span></div>
  <div class="fxi-line"><span class="lbl">3. Identification marks (1)</span> : <span class="val">${esc(idMark1)}</span></div>
  <div class="fxi-line"><span class="lbl">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;(2)</span> : <span class="val">${esc(idMark2)}</span></div>
  <div class="fxi-line"><span class="lbl">4. Father's / Husband's / Wife's Name</span> : <span class="val">${esc(father)}</span></div>
  <div class="fxi-line"><span class="lbl">5. Sex</span> : <span class="val">${esc(sex)}</span></div>
  <div class="fxi-line full"><span class="lbl">6. Residence Address</span> : <span class="val">${esc(addr)}</span></div>
  <div class="fxi-line"><span class="lbl">7. Date of birth (if available)</span> : <span class="val">${esc(dob)}</span> &nbsp;&nbsp; and / or certified age : <span class="val">${esc(ageStr)}</span> yrs</div>
  <div class="fxi-line"><span class="lbl">8. Physical fitness</span> : <span class="val">Fit for employment as adult workman</span></div>

  <div class="fxi-fit">
    Certified that the above-named workman has been personally examined by the undersigned medical practitioner. Based on the
    physical examination and enquiry of previous history tabulated below, the workman is assessed as
    <b>FIT FOR EMPLOYMENT</b> in building &amp; construction work as an <b>adult</b> at the above-named project,
    subject to the periodical medical examination required under the rules.
  </div>

  <table class="fxi-tbl">
    <thead><tr>
      <th colspan="2">Physical Examination</th>
      <th colspan="2">Enquiry of previous history</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>

  <div class="fxi-reason">
    <div class="fxi-line"><span class="lbl">9. Reason for —</span></div>
    <div class="fxi-line full"><span class="lbl">&nbsp;&nbsp;&nbsp;(1) Refusal of certificate</span> : <span class="val">${esc("Not applicable")}</span></div>
    <div class="fxi-line full"><span class="lbl">&nbsp;&nbsp;&nbsp;(2) Certificate being revoked</span> : <span class="val">${esc("Not applicable")}</span></div>
  </div>

  <div class="fxi-sigs">
    <div class="box">Signature / Left Hand Thumb impression of the building worker</div>
    <div class="box">Signature with Seal of Medical Practitioner / Office</div>
  </div>

  <div class="fxi-foot">
    <b>Notes:</b>
    <ol>
      <li>Exact details of cause of physical disability should be clearly stated.</li>
      <li>Functional / productive abilities should also be stated if a disability is recorded.</li>
      <li>Prepared in line with BOCW Central Rules (Rule 223), Schedule VII. State BOCW rule specific requirements shall apply additionally, if any.</li>
    </ol>
  </div>
</div>
  `;
}

export function printFormXI(w, contractor) {
  if (!w) return;
  const html = `${_FORM_XI_CSS}
<body>
  <div class="no-print"><button onclick="window.print()">Print Form-XI</button></div>
  ${_buildFormXI(w, contractor)}
</body>`;
  openPrintWindow(html, `Form-XI · ${w.name || "Workman"}`);
}
